import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getAllApprovalRequests } from "@grudder/apiCalls";
import dayjs from "dayjs";

export interface ApprovalRequestItem {
  _id: string;
  type: string;
  status: string;
  lead?: { _id: string; fullName?: string };
  requestedGroup?: { _id: string; name: string } | null;
  requestedUser?: { _id: string; fullName: string } | null;
  requestNote?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApprovalRequestsFilters {
  page: number;
  limit: number;
  status?: string; // REQUESTED | APPROVED | REJECTED | undefined (undefined => all)
  type?: string; // LEAD_* or undefined (undefined => all)
}

export interface ApprovalRequestsState {
  list: ApprovalRequestItem[];
  total: number;
  listLoading: string | null; // align with leads slice pattern for overlay timing
  error?: any;
}

const initialState: ApprovalRequestsState = {
  list: [],
  total: 0,
  listLoading: null,
  error: null,
};

export const fetchApprovalRequestsList = createAsyncThunk(
  "approvalRequests/fetch",
  async (filters: ApprovalRequestsFilters) => {
    const { page, limit, status, type } = filters;
    const skip = (page - 1) * limit;
    const res = await getAllApprovalRequests({ limit, skip, status, type });
    return {
      data: (res?.data ?? []) as ApprovalRequestItem[],
      total: (res?.total ?? 0) as number,
    };
  }
);

const approvalRequestsSlice = createSlice({
  name: "approvalRequests",
  initialState,
  reducers: {
    setList(
      state,
      action: PayloadAction<{ data: ApprovalRequestItem[]; total: number }>
    ) {
      state.list = action.payload.data;
      state.total = action.payload.total;
    },
    setListLoading(state, action: PayloadAction<string | null>) {
      state.listLoading = action.payload;
    },
    updateItemStatus(
      state,
      action: PayloadAction<{ id: string; status: string }>
    ) {
      state.list = state.list.map((r) =>
        r._id === action.payload.id ? { ...r, status: action.payload.status } : r
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApprovalRequestsList.pending, (state) => {
        state.listLoading = dayjs().format("YYYY-MM-DD HH:mm:ss");
        state.error = null;
      })
      .addCase(fetchApprovalRequestsList.fulfilled, (state, action) => {
        state.list = action.payload.data;
        state.total = action.payload.total;
        state.listLoading = null;
      })
      .addCase(fetchApprovalRequestsList.rejected, (state, action) => {
        state.listLoading = null;
        state.error = action.payload;
      });
  },
});

export const { setList, setListLoading, updateItemStatus } = approvalRequestsSlice.actions;
export const approvalRequestsReducer = approvalRequestsSlice.reducer;
